import { useEffect, useRef, useState } from 'react'

import ProctoringService from 'service/ProctoringService'

const PHOTO_INTERVAL_MS = 60000

const safePost = (formData) => {
  try {
    return ProctoringService.sendEvent(formData).catch(() => {})
  } catch {
    return Promise.resolve()
  }
}

const buildBaseFormData = (examSessionId, kind) => {
  const fd = new FormData()
  fd.append('exam_session_id', String(examSessionId))
  fd.append('kind', kind)
  return fd
}

const captureVideoFrame = (video) => {
  if (!video || video.readyState < 2) return null
  const w = video.videoWidth || 320
  const h = video.videoHeight || 240
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(video, 0, 0, w, h)
  return canvas
}

const blobFromCanvas = (canvas, mime, quality) =>
  new Promise((resolve) => {
    if (!canvas) {
      resolve(null)
      return
    }
    canvas.toBlob((blob) => resolve(blob), mime, quality)
  })

const tryHtml2Canvas = async () => {
  try {
    const mod = await import('html2canvas')
    return mod.default || mod
  } catch {
    return null
  }
}

const useProctoring = ({ enabled, examSessionId }) => {
  const [active, setActive] = useState(false)
  const streamRef = useRef(null)
  const videoRef = useRef(null)
  const photoTimerRef = useRef(null)
  const stoppedRef = useRef(false)

  useEffect(() => {
    if (!enabled || !examSessionId) return undefined

    let mounted = true
    stoppedRef.current = false

    const start = async () => {
      let stream = null
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240 },
          audio: false,
        })
      } catch {
        sendBeaconEvent('webcam_denied', 'alert')
        return
      }
      if (!mounted || stoppedRef.current) {
        try {
          stream.getTracks().forEach((t) => t.stop())
        } catch {}
        return
      }
      streamRef.current = stream
      const video = document.createElement('video')
      video.autoplay = true
      video.muted = true
      video.playsInline = true
      video.srcObject = stream
      try {
        await video.play()
      } catch {}
      videoRef.current = video
      setActive(true)

      const tick = async () => {
        if (stoppedRef.current) return
        const canvas = captureVideoFrame(videoRef.current)
        const blob = await blobFromCanvas(canvas, 'image/jpeg', 0.7)
        if (!blob) return
        const fd = buildBaseFormData(examSessionId, 'photo')
        fd.append('photo', blob, `photo_${Date.now()}.jpg`)
        const html2canvasFn = await tryHtml2Canvas()
        if (html2canvasFn) {
          try {
            const shotCanvas = await html2canvasFn(document.body, {
              logging: false,
              useCORS: true,
              backgroundColor: '#ffffff',
            })
            const shotBlob = await blobFromCanvas(shotCanvas, 'image/png', 0.8)
            if (shotBlob) {
              fd.append('screenshot', shotBlob, `screen_${Date.now()}.png`)
            }
          } catch {}
        }
        await safePost(fd)
      }

      setTimeout(tick, 5000)
      photoTimerRef.current = setInterval(tick, PHOTO_INTERVAL_MS)
    }

    const sendBeaconEvent = (kind, severity = 'alert') => {
      try {
        const payload = JSON.stringify({ ts: Date.now() })
        const fd = buildBaseFormData(examSessionId, kind)
        fd.append('severity', severity)
        fd.append('payload', payload)
        safePost(fd)
      } catch {}
    }

    const onVisibility = () => {
      if (document.hidden) sendBeaconEvent('tab_switch', 'alert')
    }

    const onFullscreen = () => {
      if (!document.fullscreenElement) sendBeaconEvent('fullscreen_exit', 'alert')
    }

    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('fullscreenchange', onFullscreen)

    start()

    return () => {
      mounted = false
      stoppedRef.current = true
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('fullscreenchange', onFullscreen)
      if (photoTimerRef.current) {
        clearInterval(photoTimerRef.current)
        photoTimerRef.current = null
      }
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop())
        } catch {}
        streamRef.current = null
      }
      videoRef.current = null
      setActive(false)
    }
  }, [enabled, examSessionId])

  return { active }
}

export default useProctoring
